import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { WAITLIST_CONTACT_METHODS } from "../../lib/site";
import { readSource } from "../../lib/track";
import { CALLBACK } from "../../constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", preferred_contact: "Call" };

// Compact callback form for campaign landing pages. The offer is attached
// automatically, so the visitor never picks from a service list.
export default function CallbackForm({ offer, offerLabel }) {
  const location = useLocation();
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const honeypot = useRef("");
  const openedAt = useRef(Date.now());

  useEffect(() => {
    openedAt.current = Date.now();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please confirm we can contact you about your enquiry.");
      return;
    }
    setBusy(true);
    try {
      const source = readSource(location.search, location.pathname);
      await axios.post(`${API}/waitlist`, {
        ...form,
        services: [offerLabel],
        preferred_timing: [],
        consent: true,
        ...source,
        source_offer: offer,
        website: honeypot.current,
        elapsed_ms: Date.now() - openedAt.current,
      });
      setDone(true);
      setForm(EMPTY);
      setConsent(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please call us and we'll help right away."
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={CALLBACK.success}
        className="border border-forest/25 bg-paper p-8 md:p-10"
      >
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-full bg-forest/10 inline-flex items-center justify-center">
            <Check size={18} className="text-forest" strokeWidth={2} />
          </span>
          <span className="font-meta text-forest">Request received</span>
        </div>
        <h3 className="mt-6 font-display text-2xl md:text-3xl leading-snug text-umber">
          Thank you. We'll be in touch shortly.
        </h3>
        <p className="mt-4 text-base leading-relaxed text-umber/80">
          A member of our team will contact you using your preferred method to
          arrange your visit and answer any questions.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-umber/70">
          Please note: your appointment is not booked yet. It is confirmed once
          we have spoken with you and agreed on a day and time.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={CALLBACK.form}
      className="space-y-6"
      noValidate={false}
    >
      {/* Honeypot. Hidden from people, irresistible to bots. */}
      <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          onChange={(e) => {
            honeypot.current = e.target.value;
          }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="cb-name" className="font-meta text-umber/60 block mb-2">
            Name
          </label>
          <input
            id="cb-name"
            data-testid={CALLBACK.name}
            type="text"
            required
            autoComplete="name"
            className="alae-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="cb-phone" className="font-meta text-umber/60 block mb-2">
            Phone
          </label>
          <input
            id="cb-phone"
            data-testid={CALLBACK.phone}
            type="tel"
            required
            autoComplete="tel"
            className="alae-input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
      </div>

      <div>
        <label htmlFor="cb-email" className="font-meta text-umber/60 block mb-2">
          Email
        </label>
        <input
          id="cb-email"
          data-testid={CALLBACK.email}
          type="email"
          required
          autoComplete="email"
          className="alae-input"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </div>

      <fieldset>
        <legend className="font-meta text-umber/60 mb-3">
          Preferred contact method
        </legend>
        <div className="flex flex-wrap gap-3">
          {WAITLIST_CONTACT_METHODS.map((m) => (
            <button
              key={m}
              type="button"
              data-testid={CALLBACK.contactMethod(m)}
              aria-pressed={form.preferred_contact === m}
              onClick={() => setForm({ ...form, preferred_contact: m })}
              className={`rounded-full px-6 py-3 text-[11px] tracking-[0.2em] uppercase border transition-colors min-h-[44px] ${
                form.preferred_contact === m
                  ? "bg-forest text-paper border-forest"
                  : "border-umber/30 text-umber/80 hover:border-forest hover:text-forest"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid={CALLBACK.consent}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 accent-[#2F4F3E]"
        />
        <span className="text-sm leading-relaxed text-umber/75">
          I agree that Alae Dental may contact me by phone, text or email about
          this enquiry. We never share your details with third parties.
        </span>
      </label>

      <button
        type="submit"
        data-testid={CALLBACK.submit}
        disabled={busy}
        className="w-full sm:w-auto bg-forest text-paper rounded-full px-10 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300 disabled:opacity-60 min-h-[44px]"
      >
        {busy ? "Sending" : "Request a Callback"}
      </button>
    </form>
  );
}
